import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { getToken, authHeaders } from "./AuthContext";
import { enqueue, playFromBase64AtOffset, setAdamLeague } from "../audio/auctioneerAudio";
import { nflTeamPhrase } from "../utils/snakeTemplates";
import {
  auctionNominationLine,
  auctionBidAcceptedLine,
  auctionGoingOnceLine,
  auctionGoingTwiceLine,
} from "../utils/auctionTemplates";
import { computeAuctionRoundRollover } from "../utils/auctionRoundMath";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DraftContext = createContext(null);

// [HF3 P0-2] Cross-tab + cross-surface recap-dismiss persistence. The
// DraftEndRecapModal mounts on multiple surfaces (DraftRoom +
// StandaloneDraftBoard which open in separate tabs with separate providers).
// Founder spec: "if user dismisses on one surface, modal stays dismissed on
// the other." Backed by localStorage; cross-tab via 'storage' event.
const RECAP_DISMISS_KEY = (lid) => `pgf_recap_dismissed:${lid}`;

/**
 * @param silent - when true this provider NEVER enqueues auctioneer audio.
 * The standalone board mounts its own provider; without this the board tab
 * talks over the draft room (live bug). The board is a visual surface only.
 */
export function DraftProvider({ leagueId, children, silent = false }) {
  const [draftState, setDraftState] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [auctioneerMessage, setAuctioneerMessage] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  // [PGF Draft Board Refresh v1.1 §G] Commissioner-set league default
  // board style. Hydrated from GET /api/leagues/{id} on mount, updated
  // live via the BOARD_STYLE_CHANGED WS message when the commissioner
  // changes it.
  const [leagueDefaultBoardStyle, setLeagueDefaultBoardStyle] = useState(null);
  // [PGF Draft Board Refresh v1.1 §F.3] Commissioner-id from GET /leagues.
  // BoardStyleSelector uses this + wsUser to decide which endpoint to
  // hit (league-default PUT vs per-user override PUT).
  const [leagueCommissionerId, setLeagueCommissionerId] = useState(null);
  // [Founder reconnect-parity - Feb 2026] Full-history hydration state.
  // WS payloads trim `completed_sales` to last 20 for bandwidth; this
  // ref holds the full server-authoritative history that PickHistoryTabBody
  // consumes so the History tab is complete on any (re)connect.
  const [fullPickHistory, setFullPickHistory] = useState([]);
  // [TD-101 fix - Feb 2026] Reliable untrimmed total-sales counter for
  // Adam's round-rollover math. Mirrors the proven `StandaloneDraftBoard.jsx`
  // pattern: seeded once from the untrimmed GET /full-history endpoint (via
  // hydrateHistory below), then grown incrementally from every WS payload
  // that carries `completed_sales`, deduped by a stable sale key so the
  // trimmed-to-20 window or a reconnect never double-counts or under-counts.
  const seenSaleKeysRef = useRef(new Set());
  const totalSalesCountRef = useRef(0);
  // Cursor bookkeeping for chat infinite-scroll (older messages).
  const chatCursorRef = useRef({ before: null, hasMore: true, loading: false });
  // [Phase 2c.6-C] CHAT_REJECTED transient state for the inline UX banner.
  const [chatRejected, setChatRejected] = useState(null);
  const clearChatRejected = useCallback(() => setChatRejected(null), []);
  // AI blurb fetched on each nomination
  const [nominationBlurb, setNominationBlurb]   = useState(null);
  const [nominationBlurb2, setNominationBlurb2] = useState(null);
  // celebrationPhase: null | { sale, expiresAt }
  // Single source of truth for celebration timing. Both views read this.
  const [celebrationPhase, setCelebrationPhase] = useState(null);

  // ── Persistent nomination state ───────────────────────────────────────────
  // 'nomination' never clears on SALE_COMPLETE - it persists so the SOLD
  // overlay can show the player that was just sold.
  // It only clears: 5s after SALE_COMPLETE, on DRAFT_COMPLETE, on SALE_UNDONE,
  // or is immediately replaced by NOMINATION_STARTED.
  const [nomination, setNomination] = useState(null);

  // 'soldInfo' drives the SOLD animation. Set on SALE_COMPLETE, cleared
  // immediately when next NOMINATION_STARTED fires (never bleeds onto new player).
  const [soldInfo, setSoldInfo] = useState(null);
  const soldTimerRef = useRef(null);

  // 'bridgeRecapUrl' is set when the backend fires BRIDGE_COMPLETE after the
  // bridge transitions DRAFT_COMPLETE → SEASON_ACTIVE. DraftRoom auto-routes
  // to this URL after a 5-second celebration window.
  const [bridgeRecapUrl, setBridgeRecapUrl] = useState(null);

  // [Phase 2a.2-B §4.2] mockDraftCompleteEvent - fires when the backend
  // broadcasts MOCK_DRAFT_COMPLETE (engine reached DRAFT_COMPLETE on a
  // mock-type league; bridge held). DraftRoom uses this to surface the
  // End Mock / Convert to Season modal.
  const [mockDraftCompleteEvent, setMockDraftCompleteEvent] = useState(null);

  // [Phase 2c.6-A.1] activityVoice map - parity with SnakeDraftContext.
  // Backend `_emit_activity_written` broadcasts to BOTH formats; auction
  // standalone activity types (PICK_MADE, REACH_DETECTED, STEAL_DETECTED,
  // RUN_STARTED, ROUND_RECAP) need a draft-time UI home (LiveActivityFeed).
  const [activityVoice, setActivityVoice] = useState({});

  // wsUser: server-authenticated user identity from STATE_SNAPSHOT.
  // More reliable than useAuth().user for nomination checks because it
  // comes from the JWT-validated WebSocket connection, not AuthContext
  // which may still be MOCK_ADMIN if silentTokenFetch hasn't resolved.
  const [wsUser, setWsUser] = useState(null);

  // [HF3 P0-2] Recap-dismiss state hoisted from per-surface useState so
  // multiple surfaces (DraftRoom + StandaloneDraftBoard) stay in sync via
  // localStorage + 'storage' event listener.
  const [recapDismissed, setRecapDismissedRaw] = useState(() => {
    try { return !!leagueId && localStorage.getItem(RECAP_DISMISS_KEY(leagueId)) === "1"; }
    catch { return false; }
  });
  const dismissRecap = useCallback(() => {
    setRecapDismissedRaw(true);
    try { if (leagueId) localStorage.setItem(RECAP_DISMISS_KEY(leagueId), "1"); } catch {}
  }, [leagueId]);
  useEffect(() => {
    if (!leagueId) return;
    const handler = (e) => {
      if (e.key === RECAP_DISMISS_KEY(leagueId) && e.newValue === "1") {
        setRecapDismissedRaw(true);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [leagueId]);

  // [HF4.5, TD-101 fix] Auction has no round modal - but Adam announces
  // verbally each time the nomination order completes a full pass. The
  // announcement fires from the NOMINATION_READY handler below, keyed off
  // `totalSalesCountRef` (the untrimmed sale count) being a positive
  // multiple of `nomination_order.length` - not off the WS-trimmed
  // `completed_sales.length`, which breaks past pick 20.

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  // [ADAM AUDIO batch 2] Scope live synth to this league so /tts/speak
  // resolves the league's chosen voice instead of defaulting to Voice 1.
  useEffect(() => { setAdamLeague(leagueId); }, [leagueId]);

  // [A.1.9-3] Track last-received-WS-message wall time for forced reconnect.
  const lastMessageAtRef = useRef(Date.now());

  // [Founder Item 4 - Feb 2026] Dedup key for audio resume - the
  // `started_at_ms` of the last active_audio we already played. Prevents
  // re-triggering on repeated STATE_SNAPSHOTs (e.g. hydrate paths).
  const lastResumedAudioAtRef = useRef(null);

  // [A.1.9-1] Hydration as reusable callback - see SnakeDraftContext.js.
  const hydrate = useCallback(async () => {
    if (!leagueId) return;
    try {
      const res = await axios.get(`${BACKEND}/api/activity/${leagueId}?limit=200`, {
        headers: authHeaders(),
      });
      if (!Array.isArray(res.data)) return;
      const hydrated = {};
      res.data.forEach((row) => {
        if (!row?.id || !row?.voice_text) return;
        // [A.1.8-fix + A.1.10] D.6.2 parity - auction-only after A.1.10.
        if (!["llm", "auctioneer"].includes(row.voice_source)) return;
        hydrated[row.id] = {
          voiceText: row.voice_text,
          voiceSource: row.voice_source,
          activityType: row.type,
          pickId: null,
          createdAt: row.created_at || new Date().toISOString(),
        };
      });
      setActivityVoice((prev) => ({ ...hydrated, ...prev }));
    } catch (_e) { /* non-fatal */ }
  }, [leagueId]);

  // [Founder reconnect-parity] Full pick/sales history hydration -
  // format-agnostic. Fires on mount + WS reconnect. PickHistoryTabBody
  // (and any other history-consuming surface) reads from `fullPickHistory`
  // via the DraftContext so a reconnected user sees the complete record
  // instead of the last-20 WS trim.
  const hydrateHistory = useCallback(async () => {
    if (!leagueId) return;
    try {
      const res = await axios.get(`${BACKEND}/api/draft/${leagueId}/full-history`, {
        headers: authHeaders(),
      });
      const history = Array.isArray(res?.data?.history) ? res.data.history : [];
      setFullPickHistory(history);
      // [TD-101 fix] Seed the untrimmed sale-key set from the same
      // endpoint - identical key shape to StandaloneDraftBoard.jsx so both
      // surfaces agree on what counts as one unique sale.
      history.forEach((h) => seenSaleKeysRef.current.add(String(h.request_id || h.overall_pick)));
      totalSalesCountRef.current = seenSaleKeysRef.current.size;
    } catch (_e) { /* non-fatal */ }
  }, [leagueId]);
  // Track which nomination ID the blurb was fetched for
  const blurbFetchedForRef = useRef(null);
  const lastBidTimeRef       = useRef(0);   // timestamp of last bid event
  const bidsSinceLastCallRef = useRef(0);   // frantic-mode counter
  const lastCalledBidRef     = useRef(null); // last bid amount we announced

  // [Phase 2c.6-C] Chat history backfill. Runs on mount + reconnect (mirrors
  // A.1.7 hydrate pattern). Idempotent merge by id.
  //
  // [Founder reconnect-parity - Feb 2026] Backend caps `limit` at 200
  // (chat.py Query le=200) - we request the ceiling. Users who scroll
  // to the TOP of the chat panel trigger `loadOlderChat()` below, which
  // uses the cursor to prepend older pages.
  const hydrateChat = useCallback(async () => {
    if (!leagueId) return;
    try {
      const res = await axios.get(`${BACKEND}/api/chat/${leagueId}/draft-history?limit=200`, {
        headers: authHeaders(),
      });
      const msgs = Array.isArray(res?.data?.messages) ? res.data.messages : [];
      const hasMore = !!res?.data?.has_more;
      const nextBefore = res?.data?.next_before || null;
      // Cursor bookkeeping - the oldest msg timestamp becomes the next `before`.
      chatCursorRef.current = {
        before: nextBefore,
        hasMore,
        loading: false,
      };
      if (!msgs.length) return;
      setChatMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id).filter(Boolean));
        const merged = [...msgs.filter((m) => !seen.has(m.id)), ...prev];
        // Preserve oldest-first order. Cap removed - user can scroll
        // through the full session without truncation.
        return merged;
      });
    } catch (_e) { /* non-fatal */ }
  }, [leagueId]);

  // [Founder reconnect-parity] Load an older page of chat messages using
  // the `before` cursor. Called by the ChatPanel scroll-to-top handler.
  // Idempotent, guarded by `loading` flag + `hasMore` cursor.
  const loadOlderChat = useCallback(async () => {
    if (!leagueId) return;
    const c = chatCursorRef.current;
    if (!c || c.loading || !c.hasMore || !c.before) return;
    chatCursorRef.current = { ...c, loading: true };
    try {
      const url = `${BACKEND}/api/chat/${leagueId}/draft-history?limit=200&before=${encodeURIComponent(c.before)}`;
      const res = await axios.get(url, { headers: authHeaders() });
      const msgs = Array.isArray(res?.data?.messages) ? res.data.messages : [];
      const hasMore = !!res?.data?.has_more;
      const nextBefore = res?.data?.next_before || null;
      chatCursorRef.current = { before: nextBefore, hasMore, loading: false };
      if (!msgs.length) return;
      setChatMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id).filter(Boolean));
        const older = msgs.filter((m) => !seen.has(m.id));
        return [...older, ...prev];
      });
    } catch (_e) {
      chatCursorRef.current = { ...chatCursorRef.current, loading: false };
    }
  }, [leagueId]);

  useEffect(() => {
    hydrateChat();
  }, [hydrateChat, isConnected]);

  // [Founder reconnect-parity] Hydrate full pick/sales history on mount
  // and every WS reconnect so the History tab is always complete.
  useEffect(() => {
    hydrateHistory();
  }, [hydrateHistory, isConnected]);

  // [PGF Draft Board Refresh v1.1 §G.1/G.4] Hydrate the commissioner-set
  // league default board style on mount + reconnect. GET /leagues/{id}
  // now returns `default_board_style` via _league_to_dict.
  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/leagues/${leagueId}`, {
          headers: authHeaders(),
        });
        if (cancelled) return;
        const v = res?.data?.default_board_style;
        if (typeof v === "string" && v.length > 0) {
          setLeagueDefaultBoardStyle(v);
        }
        const cid = res?.data?.commissioner_id;
        if (typeof cid === "string" && cid.length > 0) {
          setLeagueCommissionerId(cid);
        }
      } catch (_e) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [leagueId, isConnected]);

  const connect = useCallback(() => {
    const token = getToken();
    if (!token || !leagueId) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backendUrl.replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/draft/${leagueId}?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      clearTimeout(reconnectRef.current);
      // Send a PING every 25s to keep the connection alive through proxies
      const ping = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "PING" }));
      }, 25000);
      ws._pingInterval = ping;
    };
    ws.onmessage = (e) => {
      lastMessageAtRef.current = Date.now();
      try { handleMessage(JSON.parse(e.data)); } catch {}
    };
    ws.onclose = () => {
      clearInterval(ws._pingInterval);
      setIsConnected(false);
      reconnectRef.current = setTimeout(connect, 3000);
    };
    ws.onerror = () => ws.close();
  }, [leagueId]);

  const handleMessage = (msg) => {
    const { type, data, extra } = msg;

    // [Phase 2c.6-A.1.5] Adam Panel Consolidation - every auctioneer marquee
    // string is now ALSO routed into the activityVoice map with
    // voice_source='auctioneer' so the unified <LiveActivityFeed> can render
    // it alongside future LLM rows (TD-46 / D.6.1 contract amendment).
    //
    // [HF-TTS-PRERENDER Session A] `audio_b64` is the optional pre-rendered
    // ElevenLabs payload from the F4 nomination-take cache. When present
    // the TTS hook plays it directly without a `/api/tts/speak` round-trip.
    // [ADAM AUDIO foundation] `setAuctioneerMessage` is now the DISPLAY
    // channel only (MobilePaddle's marquee). Audio no longer rides on it:
    // it was a single latest-value slot, so two sayAdam calls in one WS
    // tick (SOLD then on-the-clock, welcome then first-on-clock) batched
    // into one render and the earlier line was destroyed before the TTS
    // hook ever saw it. Lines now go straight into the audio singleton's
    // serialized queue, keyed by activity type for the supersede policy.
    const sayAdam = (activityType, text, audio_b64 = null) => {
      setAuctioneerMessage(text ? { text, audio_b64 } : null);
      if (!text) return;
      const id = `auctioneer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      if (!silent) enqueue({ id, group: activityType, text, audio_b64 });
      setActivityVoice((prev) => ({
        ...prev,
        [id]: {
          voiceText: text,
          voiceSource: "auctioneer",
          activityType,
          pickId: null,
          createdAt: new Date().toISOString(),
        },
      }));
    };

    // Only sync draftState for messages that carry the full engine snapshot.
    // AUCTIONEER_MESSAGE carries only {"text":"..."} - calling setDraftState on it
    // would wipe out managers, state, nomination_order, etc. causing the board spinner
    // and "ID? mismatch" nomination bug.
    const FULL_STATE_TYPES = new Set([
      "STATE_SNAPSHOT","DRAFT_STARTED","COUNTDOWN_STARTED",
      "NOMINATION_STARTED","BIDDING_OPEN","BID_ACCEPTED","GOING_ONCE","GOING_TWICE",
      "SALE_COMPLETE","DRAFT_COMPLETE","DRAFT_PAUSED","DRAFT_RESUMED",
      "NOMINATION_SKIPPED","SALE_UNDONE","NOMINATION_READY",
    ]);
    if (data && FULL_STATE_TYPES.has(type)) setDraftState(data);

    // [TD-101 fix] Grow the untrimmed sale-key set from any WS payload
    // carrying `completed_sales` - identical dedup key shape to
    // StandaloneDraftBoard.jsx's own `seenSaleKeysRef`. Runs before the
    // switch below so NOMINATION_READY's round math always reads the
    // up-to-date total, never the WS-trimmed array length directly.
    const streamedSales = data?.completed_sales;
    if (Array.isArray(streamedSales) && streamedSales.length) {
      streamedSales.forEach((s) => {
        const key = String(s.nomination_id || s.sale_id || `${s.player?.id}-${s.sold_at}`);
        seenSaleKeysRef.current.add(key);
      });
      totalSalesCountRef.current = seenSaleKeysRef.current.size;
    }

    // [Undo item 5] The set only ever grew, so a commissioner undo left the
    // total one too high and every later round beat fired a pick early.
    // SALE_UNDONE carries the post-rollback history: drop the key it removed.
    if (type === "SALE_UNDONE") {
      const undoneKey = (() => {
        const s = msg?.extra?.sale;
        if (!s) return null;
        return String(s.nomination_id || s.sale_id || `${s.player?.id}-${s.sold_at}`);
      })();
      if (undoneKey && seenSaleKeysRef.current.delete(undoneKey)) {
        totalSalesCountRef.current = seenSaleKeysRef.current.size;
      }
    }

    // Update nomination whenever current_nomination is non-null.
    // SALE_COMPLETE sends current_nomination=null - this guard keeps the
    // last player visible so the SOLD overlay has context.
    if (data?.current_nomination) {
      setNomination(data.current_nomination);
    }

    switch (type) {
      // [PGF Draft Board Refresh v1.1 §G.4] Live update from commissioner
      // changing the league default. Clients with a user override (§F.4)
      // still resolve to their own override client-side via useBoardStyle.
      case "BOARD_STYLE_CHANGED": {
        const next = data?.new_default_board_style;
        if (typeof next === "string" && next.length > 0) {
          setLeagueDefaultBoardStyle(next);
        }
        break;
      }
      // ── Full sync (reconnect / initial load) ───────────────────────────
      case "STATE_SNAPSHOT": {
        if (msg.user) setWsUser(msg.user);
        clearTimeout(soldTimerRef.current);
        setSoldInfo(null);
        setNomination(data?.current_nomination || null);
        // [Founder Item 4] Mid-clip audio resume - the server includes
        // `active_audio` on the state snapshot ONLY while the clip is
        // still likely playing (< 30s server-side). Seek to the elapsed
        // offset and play so this client hears the exact instant the
        // rest of the league hears at that moment. Dedup by started_at.
        const aa = data?.active_audio;
        if (aa?.started_at_ms && aa.started_at_ms !== lastResumedAudioAtRef.current) {
          lastResumedAudioAtRef.current = aa.started_at_ms;
          const elapsedSec = Math.max(0, (Date.now() - aa.started_at_ms) / 1000);
          if (aa.audio_b64 && !silent) {
            // Fire-and-forget - errors resolve silently inside the helper.
            playFromBase64AtOffset(aa.audio_b64, elapsedSec).catch(() => {});
          }
          // No fallback text fetch for now; the AUCTIONEER_MESSAGE path
          // still fires for future lines. Text-only resume would need
          // a /api/tts/speak round-trip which risks stale playback.
        }
        // If entering mid-draft during NOMINATION with no active bid, show who's on the clock
        if (data?.state === "NOMINATION" && !data?.current_nomination) {
          const nomId  = data?.current_nominator_id;
          const nomMgr = nomId ? data?.managers?.[nomId] : null;
          if (nomMgr?.team_name) {
            sayAdam("AUCTIONEER_ON_CLOCK", `${nomMgr.team_name} is on the clock!`);
          }
        }
        break;
      }

      // ── Auctioneer countdown lines (server-pushed during COUNTDOWN) ──────
      case "AUCTIONEER_MESSAGE": {
        const text = data?.text;
        // [HF-TTS-PRERENDER Session A] Server may ship a pre-rendered
        // ElevenLabs MP3 inline (F4 nomination-take cache hit). When
        // present, the TTS hook plays the cached audio directly; otherwise
        // it falls back to fetching from `/api/tts/speak` with the text.
        if (text) sayAdam("AUCTIONEER_SERVER", text, data?.audio_b64 || null);
        break;
      }

      case "COUNTDOWN_STARTED":
        break;

      // ── Draft started - Adam welcome + first-on-the-clock cue ──────────
      // [Phase 2c.6-A.2.1 D-7 / RL-28] Verbatim founder welcome line.
      // Client-synthesized (zero LLM, deterministic). Fires for both Live
      // and Mock auction drafts.
      case "DRAFT_STARTED": {
        sayAdam(
          "DRAFT_STARTED_WELCOME",
          "Welcome to your draft. Adam here. Round 1 starts now."
        );
        const firstId = data?.nomination_order?.[0];
        const firstMgr = firstId ? data?.managers?.[firstId] : null;
        if (firstMgr?.team_name) {
          sayAdam("AUCTIONEER_DRAFT_STARTED", `${firstMgr.team_name} is on the clock!`);
        }
        break;
      }

      // ── New player nominated ────────────────────────────────────────────
      case "NOMINATION_STARTED": {
        clearTimeout(soldTimerRef.current);
        setSoldInfo(null);
        setNomination(data?.current_nomination);
        const p            = data?.current_nomination?.player;
        const openingBid   = data?.current_nomination?.current_bid;
        const nominatorId  = data?.current_nomination?.nominated_by;
        const nominatorMgr = nominatorId ? data?.managers?.[nominatorId] : null;
        const teamName     = nominatorMgr?.team_name || "A team";
        if (p) {
          const POS_FLAVORS = {
            QB:  ["signal caller","quarterback","field general","trigger man","gunslinger"],
            RB:  ["workhorse back","running back","ball carrier","every-down back","ground-and-pound guy"],
            WR:  ["pass-catcher","wide receiver","wideout","deep threat","route runner"],
            TE:  ["mismatch nightmare","tight end","red zone weapon","chess piece","move tight end"],
            K:   ["points machine","kicker","money leg","boot artist","automatic"],
            DEF: ["defensive wall","defense","stop unit","turnover machine","sack squad"],
          };
          const flavors  = POS_FLAVORS[p.position] || ["player"];
          const posFlavor = flavors[Math.floor(Math.random() * flavors.length)];
          // [HF4-3] Grammar: nflTeamPhrase(withArticle:true) → "of Miami" /
          // "of the Dolphins" / "of the Miami Dolphins". Mirrors snake.
          // [A.2.5] Variety: 20 nomination templates randomized per call.
          const teamPhrase = p.nfl_team
            ? nflTeamPhrase(p.nfl_team, undefined, { withArticle: true })
            : "";
          const teamFragment = teamPhrase ? ` of ${teamPhrase}` : "";
          sayAdam(
            "AUCTIONEER_NOMINATION",
            auctionNominationLine({
              team: teamName,
              player: p.name,
              flavor: posFlavor,
              teamFrag: teamFragment,
              bid: openingBid,
            }),
          );
        }

        // Fire-and-forget AI blurb for this player
        const nomId    = data?.current_nomination?.id;
        const bidTimer = data?.settings?.bid_timer ?? 30;
        if (p && nomId && nomId !== blurbFetchedForRef.current) {
          blurbFetchedForRef.current = nomId;
          setNominationBlurb(null);
          setNominationBlurb2(null);
          fetch(`${BACKEND}/api/tts/player-blurb`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              player_name:      p.name,
              position:         p.position,
              projected_points: p.projected_points || 0,
              nfl_team:         p.nfl_team || "",
              blurb_count:      bidTimer >= 60 ? 2 : 1,
            }),
          })
            .then(r => r.json())
            .then(d => {
              if (d.blurb)  setNominationBlurb(d.blurb);
              if (d.blurb2) setNominationBlurb2(d.blurb2);
            })
            .catch((_e) => null);
        }
        break;
      }

      // ── Bid events - two-rule throttle ───────────────────────────────
      case "BID_ACCEPTED": {
        const nom = data?.current_nomination;
        if (nom) {
          const mgr    = data?.managers?.[nom.current_bidder_id];
          const amount = nom.current_bid;
          const now    = Date.now();
          const timeSinceLast = now - lastBidTimeRef.current;
          lastBidTimeRef.current = now;

          if (timeSinceLast < 1000) {
            // Frantic mode: multiple bids under 1s apart - call every 3rd
            bidsSinceLastCallRef.current += 1;
            if (bidsSinceLastCallRef.current >= 3) {
              bidsSinceLastCallRef.current = 0;
              lastCalledBidRef.current = amount;
              // [A.2.5] Variety: 20 bid-accepted templates randomized.
              sayAdam(
                "AUCTIONEER_BID",
                auctionBidAcceptedLine({ team: mgr?.team_name || "A team", amount }),
              );
            }
          } else {
            // Silence mode: no bid for ≥1s - always call if not already called
            bidsSinceLastCallRef.current = 0;
            if (lastCalledBidRef.current !== amount) {
              lastCalledBidRef.current = amount;
              sayAdam(
                "AUCTIONEER_BID",
                auctionBidAcceptedLine({ team: mgr?.team_name || "A team", amount }),
              );
            }
          }
        }
        break;
      }
      case "GOING_ONCE": {
        const nom = data?.current_nomination;
        if (nom) {
          // [HF chat-tts-dedupe] Founder feedback: keep the unique LLM line,
          // drop the static MP3 clip. Previously both fired at the same
          // state-transition tick, producing audible double-tracking
          // ("going twice ... going twice, anyone want TJ Hockenson?").
          // The 5s/2.5s/2.5s cadence widening (HF-TD89 HF-4) already
          // guarantees ElevenLabs has room to finish before the next
          // transition, so the static clip is now redundant.
          // [A.2.5] Variety: 20 going-once templates randomized.
          // [ADAM AUDIO foundation] The pre-emptive stopCurrent() is GONE -
          // it cut whatever Adam was mid-sentence on. The queue plays this
          // behind the current line, and a later SOLD supersedes any
          // going-once/twice line still pending.
          sayAdam(
            "AUCTIONEER_GOING_ONCE",
            auctionGoingOnceLine({
              player: nom.player?.name,
              bid: nom.current_bid,
            }),
          );
        }
        break;
      }
      case "GOING_TWICE": {
        const nom = data?.current_nomination;
        if (nom) {
          // [HF chat-tts-dedupe] See GOING_ONCE comment above.
          sayAdam(
            "AUCTIONEER_GOING_TWICE",
            auctionGoingTwiceLine({ player: nom.player?.name }),
          );
        }
        break;
      }

      // ── Sale complete - celebration window. NOMINATION_READY fires 5s later ─
      case "SALE_COMPLETE": {
        const sale = extra?.sale;
        if (sale) {
          setSoldInfo(sale);
          setCelebrationPhase({ sale, expiresAt: Date.now() + 5000 });
          // SOLD announcement only - do NOT say "next team is on the clock" here.
          sayAdam("AUCTIONEER_SOLD", `SOLD! ${sale.player?.name} to ${sale.winner_name} for $${sale.winning_bid}!`);
          clearTimeout(soldTimerRef.current);
          // Fallback clear in case NOMINATION_READY never arrives (e.g. draft complete)
          soldTimerRef.current = setTimeout(() => {
            setSoldInfo(null);
            setNomination(null);
            setCelebrationPhase(null);
          }, 7000);
        }
        break;
      }

      case "NOMINATION_READY": {
        // Celebration window over. Clear SOLD state, announce next team on the clock.
        clearTimeout(soldTimerRef.current);
        setSoldInfo(null);
        setNomination(null);
        setCelebrationPhase(null);
        const nextIdx = data?.current_nominator_idx ?? 0;
        const nextId  = data?.nomination_order?.[nextIdx];
        const nextMgr = nextId ? data?.managers?.[nextId] : null;
        // [TD-101 fix - Feb 2026] Detect auction round rollover. A "round"
        // in auction = one full pass through `nomination_order`. After
        // every full pass, the TRUE total-sales count is a positive
        // multiple of order length -> Adam announces the new round before
        // the next on-the-clock cue. Previously this read
        // `data.completed_sales.length` directly, which the backend trims
        // to the last 20 entries (see the seenSaleKeysRef comment above) -
        // provably wrong for round 3+ on any league with >20 total picks.
        // Now reads `totalSalesCountRef`, the untrimmed counter bootstrapped
        // from GET /full-history and grown from every WS payload.
        const orderLen = (data?.nomination_order || []).length;
        const totalSales = totalSalesCountRef.current;
        const { isRoundRollover, nextRound } = computeAuctionRoundRollover(totalSales, orderLen);
        if (isRoundRollover && nextRound >= 2 && nextMgr?.team_name) {
          sayAdam(
            "AUCTIONEER_ROUND_START",
            `Round ${nextRound} begins. ${nextMgr.team_name} is on the clock!`,
          );
        } else if (nextMgr?.team_name) {
          sayAdam("AUCTIONEER_ON_CLOCK", `${nextMgr.team_name} is on the clock!`);
        }
        break;
      }

      case "DRAFT_COMPLETE":
        clearTimeout(soldTimerRef.current);
        setSoldInfo(null);
        setNomination(null);
        sayAdam("AUCTIONEER_DRAFT_COMPLETE", "THE DRAFT IS COMPLETE! What a night, folks!");
        break;

      case "DRAFT_PAUSED":
        sayAdam("AUCTIONEER_PAUSED", "The commissioner has paused the draft. Stand by...");
        break;

      case "DRAFT_RESUMED":
        sayAdam("AUCTIONEER_RESUMED", "We're back! The draft has resumed!");
        break;

      // [TD-89 v2 Verdict 8.A] DRAFT_ABANDONED - commissioner wiped draft
      // progress. fetchLeague triggers DraftRoom's status==="LOBBY" redirect
      // (DraftRoom.jsx:201) returning all connected clients to the lobby.
      case "DRAFT_ABANDONED":
        try { window.dispatchEvent(new CustomEvent("pgf:draft-abandoned", { detail: data })); } catch {}
        break;

      // [TD-89 v2 Capability E] MANAGERS_UPDATED - a manager identity changed
      // (e.g. converted to CPU). Trigger a fetchLeague refresh so connected
      // clients pick up the new identity without manual reload.
      case "MANAGERS_UPDATED":
        try { window.dispatchEvent(new CustomEvent("pgf:managers-updated", { detail: data })); } catch {}
        break;

      case "SALE_UNDONE": {
        clearTimeout(soldTimerRef.current);
        setSoldInfo(null);
        setNomination(null);
        // [Undo item 5] Name who is back on the clock - "back to nominations"
        // alone left the room guessing whose turn had been restored.
        const backIdx = data?.current_nominator_idx ?? 0;
        const backId = data?.nomination_order?.[backIdx];
        const backMgr = backId ? data?.managers?.[backId] : null;
        const backLine = backMgr?.team_name ? ` ${backMgr.team_name} is on the clock!` : "";
        const undoneName = (msg?.extra?.sale?.player?.name || "").trim();
        sayAdam(
          "AUCTIONEER_SALE_UNDONE",
          undoneName
            ? `The commissioner has reversed the ${undoneName} sale.${backLine}`
            : `The commissioner has reversed the last sale.${backLine}`,
        );
        break;
      }

      case "NOMINATION_SKIPPED": {
        // Fallback-only: fires when auto-nomination couldn't find a player
        const nextIdx2 = data?.current_nominator_idx ?? 0;
        const nextId2  = data?.nomination_order?.[nextIdx2];
        const nextMgr2 = nextId2 ? data?.managers?.[nextId2] : null;
        const nextLine2 = nextMgr2?.team_name ? ` ${nextMgr2.team_name} is on the clock!` : "";
        sayAdam("AUCTIONEER_NOMINATION_SKIPPED", `Moving on...${nextLine2}`);
        break;
      }

      case "CHAT_MESSAGE":
        // [Phase 2c.6-C] Dedupe by id when present; in-flight broadcast may
        // race history-backfill on reconnect.
        setChatMessages((prev) => {
          const incoming = data;
          if (incoming?.id && prev.some((m) => m.id === incoming.id)) return prev;
          return [...prev.slice(-199), incoming];
        });
        break;

      case "CHAT_REJECTED":
        // [Phase 2c.6-C] Server rejected our outbound message (e.g. >500 chars).
        setChatRejected({
          reason: data?.reason || "UNKNOWN",
          max_chars: data?.max_chars || 500,
          len: data?.len || 0,
          ts: Date.now(),
        });
        break;

      case "BRIDGE_COMPLETE":
        // Bridge fired - store the recap URL; DraftRoom listens for this.
        if (msg?.recap_url) {
          setBridgeRecapUrl(msg.recap_url);
        }
        break;

      case "ACTIVITY_WRITTEN": {
        // [Phase 2c.6-A.1] Parity with SnakeDraftContext - feed LiveActivityFeed.
        const activityId = msg?.activity?.id;
        const voiceText = msg?.activity?.voice_text;
        const activityType = msg?.activity?.type;
        const pickId = msg?.activity?.payload?.pick_request_id ||
                       msg?.activity?.payload?.overall_pick;
        if (activityId && voiceText) {
          setActivityVoice(prev => ({
            ...prev,
            [activityId]: {
              voiceText,
              voiceSource: msg?.activity?.voice_source,
              activityType,
              pickId,
              createdAt: msg?.activity?.created_at || new Date().toISOString(),
            },
          }));
        }
        break;
      }

      case "MOCK_DRAFT_COMPLETE":
        // [Phase 2a.2-B §4.2] Mock draft finished; bridge held pending
        // commissioner choice. DraftRoom surfaces the modal.
        setMockDraftCompleteEvent({
          league_type: msg?.league_type || null,
          at: Date.now(),
        });
        break;

      case "ROUND_STARTED": {
        // [HF4.5] No-op for auction. The backend no longer broadcasts this
        // event for auction (engines/draft.py); kept as a defensive case so
        // any stale frame from a pre-HF4.5 server is silently dropped.
        break;
      }

      case "ERROR":
        // Surface server errors in the chat panel so they're visible
        if (data?.message) {
          setChatMessages(prev => [...prev.slice(-99), {
            sender_id: "system",
            sender_name: "System",
            text: `\u26A0\uFE0F ${data.message}`,
            is_cpu: false,
            is_system: true,
            timestamp: new Date().toISOString(),
          }]);
        }
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectRef.current);
      clearTimeout(soldTimerRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  // [Phase 2c.6-A.1.5 + A.1.7-1 + A.1.9-1] Hydration - see SnakeDraftContext.js.
  useEffect(() => {
    if (!leagueId || !isConnected) return;
    hydrate();
    const ACTIVE_STATES = new Set(["NOMINATION", "COUNTDOWN", "NOMINATION_PENDING", "DRAFTING"]);
    if (!ACTIVE_STATES.has(draftState?.state)) return;
    const id = setInterval(hydrate, 60_000);
    return () => clearInterval(id);
  }, [leagueId, isConnected, draftState?.state, hydrate]);

  // [A.1.9-3] Forced reconnect on 30s WS message silence during active draft.
  useEffect(() => {
    if (!leagueId || !isConnected) return;
    const ACTIVE_STATES = new Set(["NOMINATION", "COUNTDOWN", "NOMINATION_PENDING", "DRAFTING"]);
    if (!ACTIVE_STATES.has(draftState?.state)) return;
    lastMessageAtRef.current = Date.now();
    const id = setInterval(() => {
      const idleMs = Date.now() - lastMessageAtRef.current;
      if (idleMs >= 30_000 && wsRef.current?.readyState === WebSocket.OPEN) {
        try { wsRef.current.close(); } catch (_e) {}
      }
    }, 10_000);
    return () => clearInterval(id);
  }, [leagueId, isConnected, draftState?.state]);

  const sendMessage = useCallback((type, data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, data }));
    }
  }, []);

  const placeBid = useCallback((amount) => {
    sendMessage("BID", { amount, request_id: `${Date.now()}-${Math.random()}` });
  }, [sendMessage]);

  const nominate = useCallback((playerId, openingBid = 1) => {
    sendMessage("NOMINATE", { player_id: playerId, opening_bid: openingBid });
  }, [sendMessage]);

  const sendChat = useCallback((text) => {
    sendMessage("CHAT", { text });
  }, [sendMessage]);

  // [Phase 2c.6-A.1.5] Public push helper for components (e.g. ActiveNomination's
  // mid-countdown blurb injection) - every external auctioneer line goes through
  // here so it lands in the unified feed alongside handleMessage events.
  const pushAuctioneerLine = useCallback((activityType, text) => {
    if (!text) return;
    setAuctioneerMessage(text);
    const id = `auctioneer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setActivityVoice((prev) => ({
      ...prev,
      [id]: {
        voiceText: text,
        voiceSource: "auctioneer",
        activityType,
        pickId: null,
        createdAt: new Date().toISOString(),
      },
    }));
  }, []);

  return (
    <DraftContext.Provider value={{
      leagueId,
      draftState, isConnected, auctioneerMessage, chatMessages,
      chatRejected, clearChatRejected,
      nomination, soldInfo,
      wsUser,
      nominationBlurb,
      nominationBlurb2,
      celebrationPhase,
      bridgeRecapUrl,
      mockDraftCompleteEvent,
      setMockDraftCompleteEvent,
      activityVoice,
      setAuctioneerMessage,
      pushAuctioneerLine,
      placeBid, nominate, sendChat,
      recapDismissed, dismissRecap,
      // [Founder reconnect-parity - Feb 2026]
      fullPickHistory,
      loadOlderChat,
      // [PGF Draft Board Refresh v1.1 §G] Commissioner-set default board
      // style. Consumed by useBoardStyle hook in StandaloneDraftBoard.
      leagueDefaultBoardStyle,
      leagueCommissionerId,
    }}>
      {children}
    </DraftContext.Provider>
  );
}

export const useDraft = () => useContext(DraftContext);
